import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { ToasterService } from '../../../../ui-components/services/toast/toast.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TenantService } from '../../services/tenant/tenant.service';
import { MaintenanceModeService } from '../../services/maintenance-mode/maintenance-mode.service';
import { AUTH_ROUTES } from '../../constants/auth.routing.constants';
import { ADMIN_MAIN_ROUTE } from '../../constants/admin.routing.constants';
import { LogoutHelperService } from '../../helpers/logout-helper/logout-helper.service';

const NETWORK_ERROR_PATTERN =
  /Load failed|Failed to fetch|NetworkError|Network request failed/i;

function isClientNetworkError(httpError: HttpErrorResponse): boolean {
  if (httpError.status === 0) {
    return true;
  }

  const body = httpError.error;
  if (typeof ProgressEvent !== 'undefined' && body instanceof ProgressEvent) {
    return true;
  }
  if (body instanceof TypeError) {
    return true;
  }

  const message =
    body instanceof Error
      ? body.message
      : typeof body === 'string'
        ? body
        : typeof (body as { message?: unknown } | null)?.message === 'string'
          ? (body as { message: string }).message
          : httpError.message;

  return NETWORK_ERROR_PATTERN.test(message ?? '');
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToasterService);
  const router = inject(Router);
  const maintenanceModeService = inject(MaintenanceModeService);
  const tenantService = inject(TenantService);
  const logoutHelper = inject(LogoutHelperService);

  let currentLocation = '';
  if (router.url.includes(ADMIN_MAIN_ROUTE)) {
    currentLocation = 'admin';
  }

  return next(req).pipe(
    catchError((httpError: HttpErrorResponse) => {
      const error = httpError.error;

      if (isClientNetworkError(httpError)) {
        maintenanceModeService.showMaintenanceMode.set(false);
        maintenanceModeService.showSiteRedirectNotice.set(false);
        return throwError(() => ({
          status: httpError.status || 0,
          message: 'Network request failed',
        }));
      }

      if (httpError.status == 401) {
        logoutHelper.handleLogout();
      } else if (httpError.status === 503) {
        maintenanceModeService.showMaintenanceMode.set(true);
      } else if (httpError.status === 308) {
        maintenanceModeService.showSiteRedirectNotice.set(true);
        console.log('Not Found Mode Activated', error);
      } else if (httpError.status === 403) {
        toastService.add(error.message, '', '', 'error', true, false);
        if (currentLocation === ADMIN_MAIN_ROUTE) {
          router.navigate([tenantService.adminPath()]);
        } else {
          router.navigate([tenantService.tenantPath('')]);
        }

        return throwError(() => {
          return {
            error: error,
            status: httpError.status,
          };
        });
      } else if (httpError.status === 423) {
        router.navigate([tenantService.tenantPath(AUTH_ROUTES.RESET_PASSWORD)]);
      } else if (httpError.status !== 422) {
        if (error.message) {
          toastService.add(error.message, '', '', 'error', true, false);
        }
        maintenanceModeService.showMaintenanceMode.set(false);
        maintenanceModeService.showSiteRedirectNotice.set(false);
      }
      return throwError(() => error);
    })
  );
};
